import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'

import { ProtocolName } from '../enums/protocols.ts'
import { TokenName } from '../enums/tokens.ts'
import { Token } from '../interfaces/tokens.ts'
import { tokensMap } from '../constants/tokens.ts'
import { UWU_CONTRACT_ADDRESSES } from '../constants/smartContracts.ts'
import { getArgumentsAsObject, getSwapTransaction } from '../utils/transactionUtils.ts'
import { formatUIValue, formatValue } from '../utils/formattingUtils.ts'
import { getUserTransactions } from '../infra/services/userService.ts'
import { getSwapTokenList } from '../utils/swapUtils.ts'
import { getUSMSwapAmounts, useUSMSwap } from '../infra/services/usmService.ts'
import { getUSMSwapFee } from '../utils/usmUtils.ts'
import { getUSMAlerts } from '../infra/services/alertService.ts'

import LoaderComponent from '../components/LoaderComponent.js'
import ActivityPopupComponent from '../popups/ActivityPopupComponent.js'
import DropdownTokenComponent from '../popups/DropdownTokenComponent.js'
import TransactionHandler from '../components/TransactionHandlerComponent.tsx'
import WarningComponent from '../components/WarningComponent.tsx'

import earnpr from '../img/earn-pr.png'
import warn from '../img/warn.png'
import swap from '../img/switch.png'
import loader from '../img/loader.svg'

const SavingsRatePage = () => {
  document.title = 'Savings Rate | UWU Protocol'

  const [session, user] = useOutletContext<[any, any]>()

  const newSession = session?.v2
  const newUser = user?.v2

  const transactionHandlerRef = useRef(null)

  //const { handleDepositUSR, handleWithdrawUSR } = useUSR()

  const [sendInputField, setSendInputField] = useState<string | number>('')

  const [selectedInputToken, setSelectedInputToken] = useState(tokensMap[TokenName.UWU])

  const [selectedUSRTab, setSelectedUSRTab] = useState<string>('deposit')

  const [showActivityPopup, setShowActivityPopup] = useState<boolean>(false)

  const [pendingTransactions, setPendingTransactions] = useState<any[]>([])

  const handleNumericInput = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', '.']
    const inputValue = e.currentTarget.value

    const isAllowedKey = allowedKeys.includes(e.key)
    const isSingleDot = e.key === '.' && !inputValue.includes('.')

    if (isAllowedKey && (e.key !== '.' || isSingleDot)) {
      return
    }

    if (!/^\d$/.test(e.key)) {
      e.preventDefault()
    }
  }

  const fetchPendingTransactions = async () => {
    try {
      const res = await getUserTransactions(user?.address, true)
      const filteredTransactions = res.filter((transaction: any) => {
        const { contract_call } = transaction
        const { function_name, function_args } = contract_call
        const args = getArgumentsAsObject(function_args)

        const names = ['deposit-usr', 'withdraw-usr']

        return names.includes(function_name) && contract_call.contract_id.includes(UWU_CONTRACT_ADDRESSES.DEPLOYER)
      })

      setPendingTransactions(filteredTransactions)
    } catch (error) {

    }
  }

  const handleStacksTransaction = async () => {
    //if (usmTransactionData && usmTransactionData.valid && transactionHandlerRef.current) {
    //  transactionHandlerRef.current.handleInitiate(() => handleUSMSwap(newUser, usmTransactionData), usmTransactionData)
    //}
  }

  useEffect(() => {
    if (session && user) {

      fetchPendingTransactions()
    }
  }, [user, session])

  return (
    <>
      {(session && user) ?
      <div className="core" style={{ marginTop: "auto" }}>
        <ReactTooltip className="defaultTooltip" effect="solid" multiline={true} arrowColor="#000000" padding="10px 12px" />
        <div className="vaultHeader vaultHeaderPadding">
          <div className='vaultHeaderTitle' style={{ justifyContent: 'inherit' }}>
            <a href='/earn' style={{ lineHeight: '0', marginRight: '0.5rem', fontSize: '1.25em', marginBottom: '0.1rem' }}>{`<-`}</a>
            <div style={{ display: 'flex', gridGap: '0.5rem', alignItems: 'center', marginBottom: '0', flexDirection: 'row' }}><h1 style={{ marginBottom: 'unset' }}>Savings Rate</h1><a style={{ color: '#FFFFFF', backgroundColor: '#FF7BED', padding: '3px 7px', borderRadius: '100px', fontSize: '0.65rem', textDecoration: 'none', lineHeight: 'unset', cursor: 'unset' }}>NEW</a></div>
          </div>
          <div className="vaultHeaderDivider"></div>
        </div>
        {pendingTransactions.length > 0 && <div className='pendingTxnHome pendingTxnHomeMobile' style={{ marginBottom: '15px' }}>
          <img src={loader} draggable='false' />
          <div>
            <h1>You have {pendingTransactions.length} pending transaction{pendingTransactions.length > 1 && 's'} via the Savings Rate</h1>
            <h2>Please allow 10-30 minutes for the transaction{pendingTransactions.length > 1 && 's'} to be completed.</h2>
          </div>
          <a onClick={() => setShowActivityPopup(true)} className='smallButton'>View Recent Activity</a>
        </div>}
        <div className="grid" style={{ gridTemplateColumns: "1.125fr 0.9fr" }}>
          <div className="earnDiv" style={{ maxWidth: "510px", height: "min-content" }}>
            <div className="earnDesc">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                <img draggable='false' src={earnpr} style={{ marginBottom: '0' }} />
                <a href='https://docs.uwu.cash/optional-services/savings-rate' target='_blank' className='smallButton'>Learn More</a>
              </div>
              <h1>Savings Rate</h1>
              <h2>Deposit UWU and earn the UWU Savings Rate, paid in UWU every Stacks block. No fees, minimums, and no lockups.</h2>
              <h3>What is the Savings Rate?</h3>
              <h2>The Stability Module is a tool designed to stabilize the price of UWU by enabling direct swaps between UWU and various assets, such as highly liquid stablecoins. Users can swap UWU and these stablecoins at predefined rates, typically subject to a swap fee.</h2>
              <h3>How does it work?</h3>
              <h2>The Stability Module holds reserves of UWU and various assets. Users can swap UWU for another asset, or vice versa, at predefined rates, typically subject to a swap fee. For example, if the rate between UWU and aeUSDC is 1:1, users receive 1 aeUSDC for every 1 UWU swapped, minus any swap fee.</h2>
              <h2>This approach differs from conventional Peg Stability Modules (PSMs) because it does not mint or burn UWU, meaning the assets held in the Stability Module's reserves do not back UWU.</h2>
              <div style={{ margin: "25px -25px " }} className="borrowDivider"></div>
              <div className="poolOverviewGrid" style={{ marginBottom: "20px" }}>
                <div>
                  <h2 style={{ marginBottom: "0" }}>Your Balance</h2>
                  <h1 style={{ margin: "15px 0" }}>{formatUIValue(0)} UWU</h1>
                  <h3>+0.00% since last deposit</h3>
                </div>
                <div className='stabilityReservesDiv poolCompositionDiv' style={{ borderRadius: '10px' }}>
                  <h2 style={{ marginBottom: '1rem' }}>Balance Composition</h2>
                  <div>
                    <img draggable='false' src={selectedInputToken.icon} />
                    <h1>Deposited</h1>
                    <h2>{formatUIValue(0)} {selectedInputToken.symbol}</h2>
                  </div>
                  <div>
                    <img draggable='false' src={selectedInputToken.icon} style={{ borderColor: '#ff7bed' }} />
                    <h1>Rewards</h1>
                    <h2>{formatUIValue(0)} {selectedInputToken.symbol}</h2>
                  </div>
                </div>
                <div>
                  <h2>Estimated APY</h2>
                  <h1>5.00%</h1>
                </div>
                <div>
                  <h2>Est. Yearly Rewards</h2>
                  <h1>{formatUIValue(0)} UWU</h1>
                </div>
              </div>
              <h2 style={{ marginBottom: "0.5rem" }}>System Overview</h2>
              <div className="poolOverviewGrid" style={{ marginBottom: "0" }}>
                <div>
                  <h2 style={{ marginBottom: "0" }}>Total Balance</h2>
                  <h1 style={{ margin: "15px 0" }}>{formatUIValue(0)} UWU</h1>
                  <h3>Utilization: 0.00%</h3>
                </div>
                <div className='stabilityReservesDiv poolCompositionDiv' style={{ borderRadius: '10px' }}>
                  <h2 style={{ marginBottom: '1rem' }}>Balance Composition</h2>
                  <div>
                    <img draggable='false' src={selectedInputToken.icon} />
                    <h1>Deposited</h1>
                    <h2>{formatUIValue(0)} {selectedInputToken.symbol}</h2>
                  </div>
                  <div>
                    <img draggable='false' src={selectedInputToken.icon} style={{ borderColor: '#ff7bed' }} />
                    <h1>Rewards</h1>
                    <h2>{formatUIValue(0)} {selectedInputToken.symbol}</h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="stabilityModuleSwapDiv" style={{ maxWidth: "450px" }}>
            <div className="overview borrowDisplay" style={{ height: "max-content", marginBottom: '15px' }}>
              <div className="overviewHeader manageVaultHeader">
                <div className="inner">
                  <h1 style={{ marginTop: "5px", marginBottom: "5px" }}>Manage Position</h1>
                </div>
                <div></div>
              </div>
              <div>
                <div className="manageVaultSelector" style={{ marginBottom: "20px" }}>
                  <div>
                      <a onClick={() => { setSelectedUSRTab('deposit'); setSendInputField('') }} className={`headerButton ${selectedUSRTab === 'deposit' ? 'active' : ''}`} style={{ marginRight: "5px" }}>Deposit</a>
                      <a onClick={() => { setSelectedUSRTab('withdraw'); setSendInputField('') }} className={`headerButton ${selectedUSRTab === 'withdraw' ? 'active' : ''}`} style={{ marginLeft: "5px" }}>Withdraw</a>
                  </div>
                </div>
                <div className="borrowDivider"></div>
                <div className="borrowInnerDiv manageVaultInnerDiv">
                  {selectedUSRTab === "deposit" &&
                    <div>
                      <div className='borrowInputTitle'>
                        <h1>Deposit {selectedInputToken?.symbol}</h1>
                        <h2 onClick={() => { setSendInputField(formatValue(newUser?.balances?.[selectedInputToken.name]?.formatted)) }}>Balance: {formatUIValue(newUser?.balances?.[selectedInputToken.name]?.formatted)} {selectedInputToken?.symbol}</h2>
                      </div>
                      <span className='borrowInput borrowInputPrice' style={{ marginBottom: '20px' }}><div style={{ width: '100%' }}><input inputMode='decimal' maxLength={10} type='text' placeholder={`0.00 ${selectedInputToken.symbol}`} onChange={(e) => setSendInputField(e.target.value.toString().split('.').map((el, i) => i ? el.split('').slice(0, 2).join('') : el).join('.').replace(/^0+(?=\d)/, ''))} onKeyDown={(e) => handleNumericInput(e)} value={sendInputField} /><h4>≈ ${formatUIValue(Number(sendInputField) * session?.prices?.[selectedInputToken.name])}</h4></div><div className='swapOutputCurrency' style={{ border: '0', marginRight: '12px' }}></div><div className="swapOutputCurrency" style={{ marginRight: "15px" }}><img draggable="false" src={selectedInputToken.icon} /><h1>{selectedInputToken.symbol}</h1></div></span>
                    </div>
                  }
                  {selectedUSRTab === "withdraw" &&
                    <div>
                      <div className='borrowInputTitle'>
                        <h1>Withdraw {selectedInputToken?.symbol}</h1>
                        <h2 onClick={() => { setSendInputField(formatValue(newUser?.balances?.[selectedInputToken.name]?.formatted)) }}>Maximum: {formatUIValue(newUser?.balances?.[selectedInputToken.name]?.formatted)} {selectedInputToken?.symbol}</h2>
                      </div>
                      <span className='borrowInput borrowInputPrice' style={{ marginBottom: '0px', borderRadius: '10px 10px 0px 0px' }}><div style={{ width: '100%' }}><input inputMode='decimal' maxLength={10} type='text' placeholder={`0.00 ${selectedInputToken.symbol}`} onChange={(e) => setSendInputField(e.target.value.toString().split('.').map((el, i) => i ? el.split('').slice(0, 2).join('') : el).join('.').replace(/^0+(?=\d)/, ''))} onKeyDown={(e) => handleNumericInput(e)} value={sendInputField} /><h4>≈ ${formatUIValue(Number(sendInputField) * session?.prices?.[selectedInputToken.name])}</h4></div><div className='swapOutputCurrency' style={{ border: '0', marginRight: '12px' }}></div><div className="swapOutputCurrency" style={{ marginRight: "15px" }}><img draggable="false" src={selectedInputToken.icon} /><h1>{selectedInputToken.symbol}</h1></div></span>
                      <div className="apiTable" style={{ borderTop: "none", borderRadius: "0px 0px 10px 10px", margin: "0px 20px 20px 20px" }}>
                        <a onClick={() => setSendInputField(formatValue(Math.round((newUser?.balances?.[selectedInputToken.name]?.formatted * 0.25) * 100) / 100))} className={`headerButton ${(formatValue(sendInputField) === formatValue(Math.round((newUser?.balances?.[selectedInputToken.name]?.formatted * 0.25) * 100) / 100) && newUser?.balances?.[selectedInputToken.name]?.formatted > 0) && "active"}`}>25%</a>
                        <a onClick={() => setSendInputField(formatValue(Math.round((newUser?.balances?.[selectedInputToken.name]?.formatted * 0.50) * 100) / 100))} className={`headerButton ${(formatValue(sendInputField) === formatValue(Math.round((newUser?.balances?.[selectedInputToken.name]?.formatted * 0.50) * 100) / 100) && newUser?.balances?.[selectedInputToken.name]?.formatted > 0) && "active"}`}>50%</a>
                        <a onClick={() => setSendInputField(formatValue(Math.round((newUser?.balances?.[selectedInputToken.name]?.formatted * 0.75) * 100) / 100))} className={`headerButton ${(formatValue(sendInputField) === formatValue(Math.round((newUser?.balances?.[selectedInputToken.name]?.formatted * 0.75) * 100) / 100) && newUser?.balances?.[selectedInputToken.name]?.formatted > 0) && "active"}`}>75%</a>
                        <a onClick={() => setSendInputField(formatValue(newUser?.balances?.[selectedInputToken.name]?.formatted))} className={`headerButton ${(formatValue(sendInputField) === formatValue(newUser?.balances?.[selectedInputToken.name]?.formatted) && newUser?.balances?.[selectedInputToken.name]?.formatted > 0) && "active"}`}>MAX</a>
                      </div>
                    </div>
                  }
                  {selectedUSRTab !== "overview" && <><div className="borrowDivider"></div>
                    <a onClick={() => handleStacksTransaction()} className="borrowButton" style={{ backgroundColor: true ? "#000000" : "#575757", cursor: true ? "pointer" : "default" }}>{selectedUSRTab === "deposit" ? (true) ? "Deposit" : "Enter an Amount" : true ? "Withdraw" : "Enter an Amount"}</a></>}
                </div>
              </div> 
            </div>
            <div className="overview borrowDisplay" style={{ height: "max-content" }}>
              <div className="overviewHeader">
                <h1>Position Settings</h1>
                <div></div>
              </div>
              <div className="overviewGrid2">
                <div className="vaultSettings">
                  <h2 style={{ color: "#d83434" }}>Transfer Balance</h2>
                  <h1 style={{ color: "#d83434" }}>{'>'}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      : <LoaderComponent />}
    </>
  )
}

export default SavingsRatePage